import { executeGraphQlQuery, extractNodes } from 'helpers/storefront-graphql-helper'
import { queryGetManyProductsBySKU, queryChildBundleItemsBySKU } from 'helpers/storefront-graphql-queries'

/**
 * Used to generate swatches
 * @param { Array<string> } skus
 * @returns { unknown }
 */
export const getManyProductsBySKU = async (skus) => {
  if (!skus || !(skus instanceof Array)) {
    throw new Error(`Must provide skus to getManyProductsByHandle; provided: ${typeof skus === 'object' ? JSON.stringify(skus) : skus} `)
  }

  const body = queryGetManyProductsBySKU(skus)
  return executeGraphQlQuery(body)
}

export const getChildBundleItemsBySKU = async (skus) => {
  if (!skus || !(skus instanceof Array)) {
    throw new Error(`Must provide skus to getChildBundleItemsBySKU; provided: ${typeof skus === 'object' ? JSON.stringify(skus) : skus} `)
  }

  const body = queryChildBundleItemsBySKU(skus)
  return executeGraphQlQuery(body)
}

/**
 *
 * @param { string } tag
 * @returns { query: string, variables: unknown }
 */
export const buildQueryProductsByTag = (tags = [], productFields, limit = 250) => {
  const queryTags = tags.map((tag) => `tag:${tag}`)
  const variables = { query: queryTags.join(' OR '), limit }
  const query = `
    query productsByTag($query: String, $limit: Int) {
      products(first: $limit, query: $query) {
        edges {
          node {
            ${productFields}
          }
        }
      }
    }
  `

  return { query, variables }
}

const fetchProducts = async (productFields, tags, limit, extractProduct) => {
  const gqlQuery = buildQueryProductsByTag(tags, productFields, limit)
  const products = await executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res
      return res.json()
    })
    .then((body) => extractNodes(body.data, 'products'))
    .then((data) => {
      return data.products
        .map((x) => {
          return extractProduct(x)
        })
        .sort((a, b) => (a.color > b.color ? 1 : -1))
    })
    .catch((err) => {
      console.error('Could not fetch products by styleCode tag using tag', tags, err)
      if (err.errors) {
        return err
      }
      return null
    })

  if (!products || products.errors) {
    return []
  }
  return products
}

const getColorValue = (metafield) => {
  if (!metafield) return null
  const hex = metafield.reference.fields.find((field) => field.key === 'colour_hex')?.value
  const name = metafield.reference.fields.find((field) => field.key === 'colour_name')?.value
  return { hex, name }
}

export async function getDataSwatches(tags, limit) {
  let availableTags = tags
  const sessionStorageSwatches = sessionStorage.getItem('swatches')
  const sessionSwatches = JSON.parse(sessionStorageSwatches) || {}
  if (sessionSwatches) {
    const dataTags = Object.keys(sessionSwatches)
    availableTags = tags.filter((tag) => !dataTags.includes(tag))
  }

  let dataSwatches = {}
  if (availableTags.length > 0) {
    dataSwatches = await getSwatchesByGroupTag(availableTags, limit)
  }

  const swatches = {
    ...sessionSwatches,
    ...dataSwatches
  }
  sessionStorage.setItem('swatches', JSON.stringify(swatches))
}

async function getSwatchesByGroupTag(tags, limit) {
  const productFields = `
    title
    handle
    featuredImage {
      url
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    tags
    metafields (identifiers:[{namespace: "variant_options", key: "colour"}, {namespace: "product_details", key: "size"}]){
      key
      value
      reference {
        ... on Metaobject {
          fields {
            key
            value
          }
        }
      }
    }
  `

  const extractProduct = (product) => {
    const metafieldColor = product.metafields.find((metafield) => metafield?.key === 'colour')
    const metafieldSubtitle = product.metafields.find((metafield) => metafield?.key === 'size')
    return {
      title: product.title,
      subtitle: metafieldSubtitle?.value,
      handle: product.handle,
      tags: product.tags,
      featuredImage: product.featuredImage?.url,
      price: product.priceRange.minVariantPrice.amount,
      compareAtPriceRange: parseFloat(product.compareAtPriceRange.minVariantPrice.amount).toFixed(2),
      color: getColorValue(metafieldColor)
    }
  }

  const products = await fetchProducts(productFields, tags, limit, extractProduct)

  const swatchProductsGroup = products.reduce((acc, product) => {
    const linkedTag = product.tags.find((x) => x.includes('linked-'))
    if (!linkedTag) {
      return acc
    }
    if (!acc[linkedTag]) {
      acc[linkedTag] = []
    }
    acc[linkedTag].push(product)
    return acc
  }, {})

  return swatchProductsGroup
}

export async function getMetaObjectByGid(id) {
  const query = `
  {
    metaobject(id: "${id}") {
      fields {
        value
        type
        key
      }
    }
  }
  `
  const metaobject = await executeGraphQlQuery({ query })
    .then((res) => {
      if (!res.ok) throw res
      return res.json()
    })
    .then((body) => {
      return body?.data
    })
    .catch((err) => {
      console.error('Could not fetch metaobject by gid', err)
      if (err.errors) {
        return err
      }
      return null
    })

  return metaobject
}

export function extractIdFromGid(gid) {
  return gid && Number(gid.split('/').pop())
}
